import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageModule } from 'primeng/message';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { AuthProvider, SessionsService } from '../../services/sessions.service';
import { AntiMemLeak } from '../../../../../shared/classes/anti-mem-leak';

@Component({
  selector: 'app-new-password-required',
  standalone: true,
  imports: [
    Button,
    ReactiveFormsModule,
    DividerModule,
    FloatLabelModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    CardModule,
    FormsModule,
    RouterModule,
    MessageModule,
    SelectModule,
  ],
  templateUrl: './new-password-required.component.html',
  styleUrl: './new-password-required.component.scss',
})
export class NewPasswordRequiredComponent extends AntiMemLeak implements OnInit {
  protected router: Router = inject(Router);
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected sessionService: SessionsService = inject(SessionsService);

  protected loading: boolean = false;
  protected errorMessage: string | null = null;
  protected successMessage: string | null = null;
  protected availableProviders: AuthProvider[] = [];

  protected formGroup: FormGroup = new FormGroup({
    provider: new FormControl('', [Validators.required]),
    newPassword: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/),
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
  });

  async ngOnInit(): Promise<void> {
    this.availableProviders = this.sessionService
      .getAuthenticationProviders()
      .filter((provider) => provider.confirmNewPassword !== undefined);

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        const providerName = params['provider'] || '';

        if (providerName) {
          const selectedProvider = this.availableProviders.find((p) => p.name === providerName) || null;
          if (selectedProvider) {
            this.formGroup.get('provider')?.setValue(selectedProvider);
            this.formGroup.get('provider')?.disable();
          }
        }
      })
    );

    this.formGroup.addValidators(this.passwordMatchValidator());
  }

  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const formGroup = control as FormGroup;
      const password = formGroup.get('newPassword');
      const confirmPassword = formGroup.get('confirmPassword');

      if (password && confirmPassword && password.value !== confirmPassword.value) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }

      if (confirmPassword?.hasError('passwordMismatch')) {
        confirmPassword.setErrors(null);
      }

      return null;
    };
  }

  protected async onSubmit(): Promise<void> {
    if (this.formGroup.invalid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;
    this.loading = true;

    try {
      const provider = this.formGroup.get('provider')?.value;

      if (!provider) {
        throw new Error('Please select an authentication provider');
      }

      if (!provider.confirmNewPassword) {
        throw new Error('Selected provider does not support new password confirmation');
      }

      const newPassword = this.formGroup.get('newPassword')?.value;

      await provider.confirmNewPassword(newPassword);

      this.successMessage = 'Password has been successfully updated. Redirecting to sign in...';

      setTimeout(() => {
        this.loading = false;
        this.router.navigate(['session/signin']);
      }, 2000);
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to update password. Please try again.';
      this.loading = false;
    }
  }

  protected isValid(): boolean {
    return this.formGroup.valid;
  }

  protected getPasswordError(): string | null {
    const newPassword = this.formGroup.get('newPassword');
    if (newPassword?.hasError('required') && newPassword?.touched) {
      return 'Password is required';
    }
    if (newPassword?.hasError('minlength') && newPassword?.touched) {
      return 'Password must be at least 8 characters long';
    }
    if (newPassword?.hasError('pattern') && newPassword?.touched) {
      return 'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character';
    }
    return null;
  }

  protected getConfirmPasswordError(): string | null {
    const confirmPassword = this.formGroup.get('confirmPassword');
    if (confirmPassword?.hasError('required') && confirmPassword?.touched) {
      return 'Please confirm your password';
    }
    if (confirmPassword?.hasError('passwordMismatch') && confirmPassword?.touched) {
      return 'Passwords do not match';
    }
    return null;
  }

  protected goBackToSignIn() {
    this.router.navigate(['/session/signin']);
  }
}
